import { Injectable } from '@angular/core';
import { productFruits, ProductFruitsUserObject } from 'product-fruits';
import { environment } from '@environments/environment';
import { IUser } from '@domains/auth/auth.model';
import { PermissionMap } from '@domains/role/role.model';

// Right keys that grant each product / sport. The joined names are the values
// ProductFruits segments were built on in the classic app — keep them stable.
const PRODUCT_RIGHTS: Record<string, string> = {
  ppd: 'test_create_critical_power_test',
  lactate: 'test_create_lactate',
};

const SPORT_RIGHTS: Record<string, string> = {
  cycling: 'sport_can_use_id_50_cycling',
  running: 'sport_can_use_id_53_running',
  'swimming-freestyle': 'sport_can_use_id_5_swimming_-_freestyle',
  'triathlon-running': 'sport_can_use_id_56_triathlon-running',
  'triathlon-cycling': 'sport_can_use_id_57_triathlon-cycling',
};

@Injectable({ providedIn: 'root' })
export class ProductFruitsService {
  // Substituted by CI at deploy time (see .gitlab-ci.yml); an unresolved
  // `${PRODUCT_FRUITS_WORKSPACE_CODE}` placeholder keeps ProductFruits off.
  private readonly workspaceCode = environment.productFruitsWorkspaceCode;
  private readonly enabled = !!this.workspaceCode && !this.workspaceCode.startsWith('${');

  private initializedUserId: number | null = null;

  /** Starts ProductFruits for `user`. Later calls for the same user are no-ops. */
  init(user: IUser, permissions: PermissionMap): void {
    if (!this.enabled || this.initializedUserId === user.id) return;

    if (this.initializedUserId !== null) {
      this.destroy();
    }

    productFruits.init(this.workspaceCode, 'en', this.toUserObject(user, permissions));
    this.initializedUserId = user.id;
  }

  destroy(): void {
    if (this.initializedUserId === null) return;

    window.productFruits?.services?.destroy();
    this.initializedUserId = null;
  }

  // Field values mirror the classic app's GTM tag (strings throughout, email as
  // `username`) so existing ProductFruits users and segments keep matching.
  private toUserObject(user: IUser, permissions: PermissionMap): ProductFruitsUserObject {
    const props: Record<string, string> = {
      user_id: `${user.id}`,
      username: user.username,
      country_id: `${user.organization.address_country_id}`,
      role_id: `${user.role.id}`,
      is_owner: `${user.role.is_owner}`,
      products: grantedKeys(PRODUCT_RIGHTS, permissions),
      sports: grantedKeys(SPORT_RIGHTS, permissions),
      date_joined: user.date_joined,
    };
    // Only the login response carries these until /accounts/current/ does too.
    if (user.users) props['number_users'] = `${user.users}`;
    if (user.country_name) props['country_name'] = user.country_name;

    return {
      username: user.email,
      email: user.email,
      firstname: user.first_name,
      lastname: user.last_name,
      signUpAt: user.date_joined,
      role: user.role.name,
      props,
    };
  }
}

function grantedKeys(rights: Record<string, string>, permissions: PermissionMap): string {
  return Object.entries(rights)
    .filter(([, right]) => permissions[right])
    .map(([key]) => key)
    .join('_and_');
}
